/**
 * Theme selection.
 *
 * The Editorial palette is light-first with a derived dark, and `tokens.css` already
 * carries `:root[data-theme='light']` and `:root[data-theme='dark']` blocks so an
 * explicit choice can beat the system preference. Nothing ever set that attribute,
 * which meant the light design the owner chose was unreachable on a machine set to
 * dark: it simply looked like the old app.
 *
 * Three states rather than two. A plain on/off switch has to pick a starting side and
 * silently overrides the OS from then on, which is wrong on a phone that flips at
 * sunset. `system` stays genuinely reactive.
 */

export type ThemeMode = 'system' | 'light' | 'dark';

export const THEME_MODES: readonly ThemeMode[] = ['system', 'light', 'dark'];

/** localStorage key. Namespaced so it cannot collide with upstream's own state. */
export const THEME_KEY = 'mo-os:theme';

export function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);
}

/**
 * The stored choice, or DARK when absent or corrupt.
 *
 * Dark is the default at the owner's decision, 2026-08-03: the dashboard he designed
 * is dark, and defaulting to `system` meant a machine set to light would open on a
 * palette the product is no longer primarily tuned for. `system` remains selectable,
 * so anyone who wants to follow the OS still can, and light is one click away.
 *
 * Never throws. This runs before React mounts, and in a private-mode browser reading
 * localStorage can itself raise, which would blank the page.
 */
export function readMode(storage: Pick<Storage, 'getItem'> | undefined = safeStorage()): ThemeMode {
  try {
    const raw = storage?.getItem(THEME_KEY);
    return isThemeMode(raw) ? raw : 'dark';
  } catch {
    return 'dark';
  }
}

export function writeMode(mode: ThemeMode, storage: Pick<Storage, 'setItem'> | undefined = safeStorage()): void {
  try {
    storage?.setItem(THEME_KEY, mode);
  } catch {
    // A theme preference is not worth breaking a render over.
  }
}

function safeStorage(): Pick<Storage, 'getItem' | 'setItem'> | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * Reflect the mode onto the document root.
 *
 * `system` REMOVES the attribute rather than resolving it to a concrete value. That is
 * the whole point: with no attribute the `@media (prefers-color-scheme)` block applies
 * and keeps tracking the OS, whereas writing a resolved value would freeze it at
 * whatever it happened to be when the page loaded.
 */
export function applyMode(mode: ThemeMode, root: { setAttribute(k: string, v: string): void; removeAttribute(k: string): void }): void {
  if (mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);
}

/** Cycle order for the toggle button. */
export function nextMode(mode: ThemeMode): ThemeMode {
  const i = THEME_MODES.indexOf(mode);
  return THEME_MODES[(i + 1) % THEME_MODES.length];
}

/**
 * What the user would actually see, which the button label needs in `system` mode.
 * Falls back to light when `matchMedia` is unavailable, matching the light-first
 * default in `:root`.
 */
export function resolveMode(mode: ThemeMode, prefersDark?: boolean): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return prefersDark === true ? 'dark' : 'light';
}

/** Read the OS preference. Guarded: jsdom and older browsers lack matchMedia. */
export function systemPrefersDark(): boolean {
  try {
    return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
  } catch {
    return false;
  }
}

/**
 * Apply the stored mode as early as possible.
 *
 * Called from the entry module before React renders, so the correct palette is in
 * place for the first paint. Doing it inside a component causes a visible flash of the
 * wrong theme on every load.
 */
export function initTheme(root?: { setAttribute(k: string, v: string): void; removeAttribute(k: string): void }): ThemeMode {
  const mode = readMode();
  const el = root ?? globalThis.document?.documentElement;
  if (el) applyMode(mode, el);
  return mode;
}
